import { Box, Button, Typography, Divider } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ChainLevel } from './types';
import { PositionCell } from './PositionCell';
import { palette } from 'src/theme/style/palette';
import { useMemo } from 'react';

interface Props {
    level: ChainLevel;
    currency: string;
    levelCost: string | number;
}

/**
 * Renders one chain level:
 *  - mentor centered at top
 *  - child slots in a row below
 *  - "Перейти к столу" button (from mentor link)
 */
export const ChainLevelView = ({ level, currency, levelCost }: Props) => {
    const mentor = level.positions[0];
    const mentorLink =
        mentor && typeof mentor !== 'boolean' ? mentor.link : undefined;

    const childSlots = level.positions.slice(1);
    const usedKeys = useMemo(() => {
        const keys = new Set<string>();
        level.rewards.forEach((r) => {
            if (!r) return;
            if (r.main_award) keys.add('main_award');
            if (r.referal_1 || r.referal_2 || r.referal_3) keys.add('referal');
            if (r.clone_reward) keys.add('clone_reward');
            if (r.upgrade) keys.add('upgrade');
        });
        return keys;
    }, [level.rewards]);

    const activeLegend = ALL_LEGEND.filter((l) => usedKeys.has(l.key));

    return (
        <Box sx={{ display: 'flex', width: '100%', minWidth: 0, height: '100%' }}>
            <Box sx={{
                width: '100%',
                minWidth: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Level label */}
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        textAlign: 'center',
                        color: palette.blue,
                        mb: 1,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        fontSize: '0.95rem',
                        fontWeight: 500
                    }}
                >
                    {level.name_program} {level.name_level}
                </Typography>

                {/* Mentor */}
                <Box display="flex" justifyContent="center" mb={0.5}>
                    <PositionCell position={mentor} isMentor currency={currency} />
                </Box>

                {/* Child slots */}
                <Box display="flex" justifyContent="center" alignItems="flex-start" gap={1} mb={1.5} flexWrap="wrap">
                    {childSlots.map((pos, idx) => (
                        <PositionCell key={idx} position={pos} isMentor={false}
                            reward={level.rewards[idx]} slotIndex={idx + 1} currency={currency} />
                    ))}
                </Box>

                <RewardLegend items={activeLegend} />

                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ mt: 'auto' }}>
                    {mentorLink ? (
                        <Button variant="contained" fullWidth href={mentorLink} target="_blank"
                            component="a" size="small"
                            startIcon={<OpenInNewIcon sx={{ fontSize: '0.9rem' }} />}
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.75 }}>
                            Перейти к столу
                        </Button>
                    ) : (
                        <Button variant="outlined" fullWidth disabled size="small"
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.75 }}>
                            Перейти к столу
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ─── Reward legend ────────────────────────────────────────────────────────

const ALL_LEGEND = [
    { key: 'main_award', color: '#3BA27A', label: 'Выплата' },
    { key: 'referal', color: '#818DD6', label: 'Рефер.' },
    { key: 'clone_reward', color: '#C975A7', label: 'ПКлон' },
    { key: 'upgrade', color: '#00ABCB', label: 'Апгрейд' },
];

const RewardLegend = ({ items }: { items: typeof ALL_LEGEND }) => {
    if (items.length === 0) return null;
    return (
        <Box display="flex" flexWrap="wrap" gap={1.1} mb={1.5} justifyContent="center">
            {items.map((l) => (
                <Box key={l.key} display="flex" alignItems="center" gap={0.4}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.82rem', color: 'text.main' }}>
                        {l.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};